import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Clock, Check, X, RotateCcw, AlertTriangle, Sunrise, Palmtree, LogOut, Utensils, UserPlus } from 'lucide-react';
import Modal from './Modal';
import { useLanguage } from '../context/LanguageContext';

// Shown during calculation. Nothing is rounded automatically anymore: every day is paid with the
// real punch unless somebody types another time HERE, so this modal lists EVERY day of the range
// that has a clock-in — one editable time field each — instead of only the anomalous ones.
//
// Each day still carries its flag so the days worth a second look stand out:
//   - early    (orange) → clocked in BEFORE the shift start
//   - late     (orange) → clocked in more than 30 min AFTER it (a "retardo")
//   - auth     (green)  → an authorization already sets the time; the field opens with it
//   - manual   (blue)   → the clock-in was captured/corrected by hand from the Punches screen
//   - absence  (purple) → the day also carries an approved vacation / PTO / holiday. The employee
//                         punched on a day that was supposed to be off. For a PERM employee with
//                         PTO hours the day pays what was worked PLUS the absence hours, so the row
//                         is listed for context and carries its lunch checkbox, but no time fields:
//                         the In/Out of a day off are not decided from here.
// A day with no flag is a normal day, listed and editable all the same.
//
// Each day also has an editable clock-OUT, on the same terms. It matters most where there is no
// exit punch at all (`noOut`): as things stand that day pays the provisional "by law" hours, and
// typing the Out here is what turns it into a normal calculation (out − in − lunch) without having
// to capture a punch from the Punches screen.
//
// The lunch checkbox works the same way: abre SIEMPRE marcada — la jornada normal lleva sus 30 min
// de comida — y solo los dias que alguien DESMARCA viajan de vuelta, asi que el default sigue
// aplicando en todo lo que no se toco.
//
// onConfirm({ paidIn, paidOut, lunch }) where the first two are
// { "badge_facility_YYYY-MM-DD": "HH:MM:SS" } and lunch is { same key: true|false }, each carrying
// ONLY the days that differ from the default — the same key the backend builds per
// employee+facility+day, so each decision applies to that exact date.
export default function EarlyArrivalsModal({ isOpen, onClose, employees = [], onConfirm, loading }) {
  const { t } = useLanguage();
  // dayKey -> "HH:MM:SS" the day will be PAID with. Seeded with what the scan proposes (a time typed
  // in a previous calculation, an authorized time, or the punch itself).
  const [times, setTimes] = useState({});
  // dayKey -> "HH:MM:SS" the day will be paid UP TO. Empty on a day with no exit punch until
  // somebody types one, which is precisely the case this field exists for.
  const [outTimes, setOutTimes] = useState({});
  // dayKey -> whether the 30-min lunch comes off this day. Seeded con el default (marcada), so an
  // untouched checkbox and no checkbox at all mean the same thing.
  const [lunches, setLunches] = useState({});
  // Which lunch checkboxes are an EXCEPTION to the default — the only ones that travel back.
  // Seeded with the days already overridden on the timecard, so re-confirming the modal without
  // touching anything does not silently hand them back to the rule.
  const [lunchTouched, setLunchTouched] = useState(() => new Set());
  // Los empleados que se DEJAN FUERA del calculo, por "badge_facility". Todos entran por defecto:
  // desmarcar es la excepcion, igual que la casilla de comida. Lo que se manda al backend como
  // `paidInBadges` son los que quedan marcados, y con eso el resto conserva intactas sus horas
  // escritas a mano en un calculo anterior — el backend ya lo hace asi para Reportes (authBadges).
  const [excluded, setExcluded] = useState(() => new Set());
  // Alta de un badge que marco sin estar en Empleados, sin salir del calculo. Por "badge_facility":
  // `regForms` es el formulario abierto (undefined = cerrado), `regSaving`/`regError` su estado, y
  // `registered` los que ya se dieron de alta en esta sesion del modal — el scan que los trajo
  // marcados como inexistentes ya no se vuelve a pedir, asi que la pantalla lo recuerda ella misma.
  const [regForms, setRegForms] = useState({});
  const [regSaving, setRegSaving] = useState({});
  const [regError, setRegError] = useState({});
  const [registered, setRegistered] = useState({});

  const dayKey = (emp, d) => `${emp.badge}_${emp.facility}_${d.date}`;
  const empKey = (emp) => `${emp.badge}_${emp.facility}`;
  const isIncluded = (emp) => !excluded.has(empKey(emp));

  // Sigue sin existir en Empleados: lo que dijo el scan, menos lo que se acaba de dar de alta aqui.
  const isUnregistered = (emp) => !!emp.unregistered && !registered[empKey(emp)];

  const toggleEmp = (emp) => setExcluded(s => {
    const next = new Set(s);
    const k = empKey(emp);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  // Un dia con vacaciones/PTO que PAGA (PERM con horas) suma lo trabajado MAS las horas de la
  // ausencia. Los punches se siguen mostrando — ver que alguien marco en mitad de sus vacaciones es
  // justo el motivo de listar el dia — pero sin campos de hora: el In y el Out de un dia libre no se
  // deciden desde aqui. Su comida SI, que es lo unico que cambia lo trabajado de ese dia.
  const isPtoDay = (d) => !!(d.absence && d.absence.pays);

  // A vacation day with no punches is listed for context only: there is no clock-in to decide, so
  // it has no field, is never counted, and never travels back.
  const isEditable = (d) => !d.noPunch && !!d.punch && !isPtoDay(d);

  // La casilla de lunch va en TODO dia con punches — ausencia, PTO y festivo incluidos. El
  // descuento de 30 min se le aplica a lo trabajado de ese dia igual que en una jornada normal, asi
  // que esconder la casilla dejaba dias a los que se les quitaba media hora sin forma de evitarlo.
  // Quien calcula decide si va o no, dia por dia. Deliberadamente NO depende de isEditable: un dia
  // con PTO no deja escribir su In/Out y aun asi lleva su comida.
  const hasLunchBox = (d) => !d.noPunch && !!d.punch;

  // Open with the times already decided for these timecards pre-loaded, so re-calculating a range
  // keeps what was typed last time instead of resetting every field to the raw punch.
  useEffect(() => {
    if (!isOpen) return;
    const seed = {};
    const seedOut = {};
    const seedLunch = {};
    const seedTouched = new Set();
    for (const emp of employees) {
      for (const d of emp.days) {
        const k = dayKey(emp, d);
        // La comida se siembra aparte de las horas: un dia con PTO no tiene campos de In/Out pero si
        // casilla, y antes este `continue` lo dejaba sin estado y la casilla abria en el default.
        if (hasLunchBox(d)) {
          seedLunch[k] = !!d.lunch;
          if (d.lunchEdited) seedTouched.add(k);
        }
        if (!isEditable(d)) continue;
        seed[k] = d.paidIn || d.punch;
        seedOut[k] = d.paidOut || d.punchOut || '';
      }
    }
    setTimes(seed);
    setOutTimes(seedOut);
    setLunches(seedLunch);
    setLunchTouched(seedTouched);
    setExcluded(new Set());   // cada apertura arranca con todos incluidos
    setRegForms({});
    setRegSaving({});
    setRegError({});
    setRegistered({});
  }, [isOpen, employees]);

  // Abrir el alta de un badge. El formulario arranca vacio a proposito: los punches guardados solo
  // traen el numero de gafete (el nombre venia en el archivo del checador y no se almacena), asi
  // que el nombre lo tiene que poner quien registra.
  const openReg = (emp) => {
    const k = empKey(emp);
    setRegError(s => ({ ...s, [k]: null }));
    setRegForms(s => ({
      ...s,
      [k]: s[k] || {
        firstName: '', lastName: '', type_emp: '',
        department_emp: '', company_emp: '',
        shift_start: '07:00', shift_end: '15:30',
      },
    }));
  };

  const closeReg = (emp) => {
    const k = empKey(emp);
    setRegForms(s => { const next = { ...s }; delete next[k]; return next; });
    setRegError(s => ({ ...s, [k]: null }));
  };

  const setRegField = (emp, field, value) => {
    const k = empKey(emp);
    setRegForms(s => ({ ...s, [k]: { ...s[k], [field]: value } }));
  };

  const saveReg = async (emp) => {
    const k = empKey(emp);
    const f = regForms[k];
    if (!f) return;
    if (!f.firstName.trim() || !f.lastName.trim()) {
      setRegError(s => ({ ...s, [k]: t('early.regNameReq') }));
      return;
    }
    if (!f.type_emp) {
      setRegError(s => ({ ...s, [k]: t('early.regTypeReq') }));
      return;
    }
    setRegSaving(s => ({ ...s, [k]: true }));
    setRegError(s => ({ ...s, [k]: null }));
    try {
      await axios.post('/api/employees', {
        badge_num_emp: emp.badge,
        name_emp: f.firstName.trim(),
        last_name_emp: f.lastName.trim(),
        department_emp: f.department_emp || '',
        company_emp: f.company_emp || '',
        location_emp: emp.facility,
        type_emp: f.type_emp,
        shift_start: `${(f.shift_start || '07:00').substring(0, 5)}:00`,
        shift_end: `${(f.shift_end || '15:30').substring(0, 5)}:00`,
      });
      // El turno recien capturado es el que la tarjeta debe mostrar: es contra el que se van a medir
      // las entradas de este empleado en el proximo calculo.
      setRegistered(s => ({
        ...s,
        [k]: { name: `${f.firstName.trim()} ${f.lastName.trim()}`, shiftStart: `${(f.shift_start || '07:00').substring(0, 5)}:00` },
      }));
      setRegForms(s => { const next = { ...s }; delete next[k]; return next; });
    } catch (err) {
      setRegError(s => ({ ...s, [k]: err.response?.data?.error || t('early.regError') }));
    } finally {
      setRegSaving(s => ({ ...s, [k]: false }));
    }
  };

  // Los badges que el calculo va a pagar sin que exista el empleado. Es el aviso de arriba: un chip
  // dentro de una tarjeta se pierde entre veinte empleados, y esto es lo que hay que ver al abrir.
  const unregisteredEmps = useMemo(
    () => employees.filter(e => !!e.unregistered && !registered[`${e.badge}_${e.facility}`]),
    [employees, registered]);

  const setTime = (key, value) => setTimes(s => ({ ...s, [key]: value }));
  const setOutTime = (key, value) => setOutTimes(s => ({ ...s, [key]: value }));

  // Flipping the checkbox is what makes the day an exception; the reset below is what takes it back.
  const setLunch = (key, value) => {
    setLunches(s => ({ ...s, [key]: value }));
    setLunchTouched(s => new Set(s).add(key));
  };

  // Devolver el dia al default: basta con dejar de considerarlo una excepcion, porque la casilla
  // pasa a leerse de autoLunch().
  const resetLunch = (key) => {
    setLunchTouched(s => { const next = new Set(s); next.delete(key); return next; });
  };

  // A day counts as edited when a time it will be paid with is not the punch the employee made.
  // For the Out, a day with no exit punch counts as edited the moment anything is typed: there was
  // no punch to match, and that decision is the whole point.
  const isEditedIn = (emp, d) => {
    if (!isEditable(d)) return false;
    const v = times[dayKey(emp, d)];
    return !!v && v !== d.punch;
  };

  const isEditedOut = (emp, d) => {
    if (!isEditable(d)) return false;
    const v = outTimes[dayKey(emp, d)];
    return !!v && v !== d.punchOut;
  };

  // "HH:MM:SS" / "HH:MM" -> segundos del dia. Null en lo que no sea una hora.
  const toSecs = (hms) => {
    const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(hms || '').trim());
    if (!m) return null;
    return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3] || 0);
  };

  // "13:05:00" -> "1:05 PM". El error que este chequeo caza es de AM/PM, asi que decirlo en 24 h
  // seria pedirle al usuario que haga de cabeza justo la conversion en la que se equivoco.
  const fmt12 = (hms) => {
    const s = toSecs(hms);
    if (s === null) return String(hms || '');
    const h24 = Math.floor(s / 3600);
    const mi = Math.floor((s % 3600) / 60);
    const ap = h24 < 12 ? 'AM' : 'PM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${String(mi).padStart(2, '0')} ${ap}`;
  };

  // El dia cuya SALIDA no es posterior a su ENTRADA. Casi siempre es el clic que falto en el campo
  // de hora: se escribe la salida de las 3 de la tarde como 3:00 AM y queda antes de la entrada.
  // El calculo no sabe de turnos que cruzan la medianoche — hace out − in, lo que da negativo, lo
  // deja en 0 y ademas BORRA el In y el Out del dia (ver calculateTimecardsForWeek), asi que ese
  // dia acaba sin horas y en blanco en el timecard exportado, sin nada que explique por que. Se
  // avisa aqui, que es donde se escribio, en vez de dejarlo aparecer como un hueco en el reporte.
  const hasBadSpan = (emp, d) => {
    if (!isEditable(d)) return false;
    const k = dayKey(emp, d);
    const i = toSecs(times[k]);
    const o = toSecs(outTimes[k]);
    if (i === null || o === null) return false; // un Out vacio es "sin decidir", no un error
    return o <= i;
  };

  // Los dias mal capturados de los empleados que SI entran al calculo: los de un empleado
  // desmarcado no viajan, asi que no tienen por que detener el boton.
  const badSpanDays = useMemo(() => {
    const out = [];
    for (const emp of employees) {
      if (!isIncluded(emp)) continue;
      for (const d of emp.days) if (hasBadSpan(emp, d)) out.push({ emp, d });
    }
    return out;
  }, [employees, times, outTimes, excluded]);

  // El default de la casilla: SIEMPRE marcada. Una jornada normal lleva sus 30 min de comida, y el
  // dia que no la tomo es la excepcion que se desmarca a mano. Es el mismo default que aplica
  // lunchApplies() en el backend, y al ser una constante no puede desviarse de el — antes dependia
  // del bruto contra el umbral de la facilidad y habia que recalcularla en vivo con lo que
  // hubiera en los campos para que la casilla no prometiera una cosa y el calculo hiciera otra.
  const autoLunch = () => true;

  // Lo que la casilla muestra: la decision a mano si la hay, y si no el default.
  const lunchChecked = (emp, d) => {
    const k = dayKey(emp, d);
    return lunchTouched.has(k) ? !!lunches[k] : autoLunch();
  };

  const isEditedLunch = (emp, d) => hasLunchBox(d) && lunchTouched.has(dayKey(emp, d));

  const isEdited = (emp, d) => isEditedIn(emp, d) || isEditedOut(emp, d) || isEditedLunch(emp, d);

  const editedCount = useMemo(() => {
    let n = 0;
    for (const emp of employees) for (const d of emp.days) if (isEdited(emp, d)) n++;
    return n;
  }, [employees, times, outTimes, lunchTouched]);

  const dayCount = useMemo(
    () => employees.reduce((s, e) => s + e.days.filter(isEditable).length, 0), [employees]);

  const includedCount = useMemo(
    () => employees.filter(isIncluded).length, [employees, excluded]);
  const excludedCount = employees.length - includedCount;

  // Bulk shortcuts over one employee's days: back to the real punch, or all from the shift start.
  // Only the In has a shift to fall back on; "all actual times" resets both sides.
  const setEmpAll = (emp, mode) => {
    setTimes(s => {
      const next = { ...s };
      for (const d of emp.days) {
        if (!isEditable(d)) continue;
        if (mode === 'punch') next[dayKey(emp, d)] = d.punch;
        else if (emp.shiftStart) next[dayKey(emp, d)] = emp.shiftStart;
      }
      return next;
    });
    if (mode !== 'punch') return;
    setOutTimes(s => {
      const next = { ...s };
      for (const d of emp.days) {
        if (!isEditable(d)) continue;
        next[dayKey(emp, d)] = d.punchOut || '';
      }
      return next;
    });
    // "All actual times" also hands the lunch of every day back to the default (marcada).
    setLunchTouched(s => {
      const next = new Set(s);
      for (const d of emp.days) {
        if (!hasLunchBox(d)) continue;
        next.delete(dayKey(emp, d));
      }
      return next;
    });
  };

  const handleConfirm = () => {
    // Una salida anterior a su entrada no se manda a calcular: el backend la pagaria como 0 h y el
    // dia saldria vacio en el reporte. El boton ya viene apagado en ese caso; esto es el cinturon.
    if (badSpanDays.length > 0) return;
    // Only the days that differ from the punch travel: everything else is the default and needs no
    // decision. An authorized day left at its authorized time is not sent either — the calculation
    // applies the authorization on its own, and sending it would freeze a time that the
    // authorization may still change.
    const paidIn = {};
    const paidOut = {};
    const lunch = {};
    // Solo los empleados marcados. `badges` viaja como paidInBadges: le dice al calculo sobre
    // quienes se decidio, para que los desmarcados no pierdan lo que ya tenian guardado.
    const included = employees.filter(isIncluded);
    const badges = [...new Set(included.map(e => String(e.badge)))];
    for (const emp of included) {
      for (const d of emp.days) {
        const k = dayKey(emp, d);
        // Only the checkboxes that were flipped: every other day stays with the default, asi que
        // en la practica solo viajan los dias que alguien desmarco. Va antes del `continue` porque
        // un dia con PTO tiene comida que decidir aunque no tenga In ni Out editables.
        if (hasLunchBox(d) && lunchTouched.has(k)) lunch[k] = !!lunches[k];
        if (!isEditable(d)) continue;
        const v = times[k];
        if (v && v !== d.punch && !(d.authorized && d.authTime && v === d.authTime)) {
          paidIn[k] = v;
        }
        // The Out has no authorization to defer to: anything that is not the exit punch travels,
        // including a time typed on a day that had no exit punch at all.
        const vOut = outTimes[k];
        if (vOut && vOut !== d.punchOut) paidOut[k] = vOut;
      }
    }
    onConfirm({ paidIn, paidOut, lunch, badges });
  };

  const fmtDate = (ds) => { const [y, mo, d] = ds.split('-'); return `${Number(mo)}/${Number(d)}/${y}`; };

  // Minutos -> "3:38 h". Las horas que paga un dia se leen mucho mejor asi que en minutos sueltos.
  const fmtHm = (mins) => `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')} h`;

  // The flag of one day: why it might deserve a different time than the punch.
  const dayFlag = (d) => {
    if (d.kind === 'early') {
      return { cls: 'rowchip-warn', icon: <Sunrise size={11} />, text: `${t('early.flagEarly')} ${d.minsEarly} min` };
    }
    if (d.kind === 'late') {
      return { cls: 'rowchip-warn', icon: <AlertTriangle size={11} />, text: `${t('early.flagLate')} +${d.minsLate} min` };
    }
    if (d.manual) {
      return { cls: 'rowchip-info', icon: <Clock size={11} />, text: t('early.flagManual') };
    }
    return null;
  };

  // fecha | entrada real | salida real | · | alertas | se paga desde | se paga hasta | lunch | ↺
  // El separador parte el renglon en dos mitades legibles: lo que PASO y lo que se PAGA.
  const GRID = { gridTemplateColumns: '84px 78px 78px 12px minmax(120px, 1fr) 112px 112px 46px 28px' };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('early.title')} xl>
      <div className="modal-body">
        <div className="msection-head">
          <span>{employees.length} {employees.length === 1 ? t('early.employee') : t('early.employees')}</span>
          <span className="msection-count">{dayCount} {t('early.editableDays')}</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 6, fontSize: 13.5, lineHeight: 1.5 }}>{t('early.description')}</p>
        <p className="msection-hint" style={{ marginBottom: 16 }}>{t('early.dayHint')}</p>

        {/* Lo unico de esta pantalla que IMPIDE calcular, asi que va hasta arriba: mientras haya un
            dia con la salida antes de la entrada el boton esta apagado, y sin este aviso el usuario
            no tendria de donde saber por que. */}
        {badSpanDays.length > 0 && (
          <div style={{
            marginBottom: 16, padding: 12, borderRadius: 'var(--radius)',
            background: 'rgba(239,68,68,0.10)', border: '1px solid var(--danger)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600 }}>
              <AlertTriangle size={15} style={{ color: 'var(--danger)' }} />
              <span>
                {badSpanDays.length}{' '}
                {badSpanDays.length === 1 ? t('early.badSpanWarnOne') : t('early.badSpanWarnMany')}
              </span>
            </div>
            <p style={{ margin: '6px 0 8px', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('early.badSpanWarnDesc')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {badSpanDays.slice(0, 8).map(({ emp, d }) => (
                <span key={`${emp.badge}_${emp.facility}_${d.date}`} className="badge badge-danger"
                  style={{ fontSize: 11 }}>
                  Badge {emp.badge} · {fmtDate(d.date)} · {fmt12(times[dayKey(emp, d)])} → {fmt12(outTimes[dayKey(emp, d)])}
                </span>
              ))}
              {badSpanDays.length > 8 && (
                <span style={{ fontSize: 11.5, color: 'var(--text-muted)', alignSelf: 'center' }}>
                  {t('early.badSpanMore').replace('{n}', badSpanDays.length - 8)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Badges con punches que no existen en Empleados. Va arriba de todo porque es lo unico de
            esta pantalla que no se arregla escribiendo una hora: o se da de alta a la persona o se
            averigua de quien es el gafete, y en cualquier caso el calculo ya le esta pagando. */}
        {unregisteredEmps.length > 0 && (
          <div style={{
            marginBottom: 16, padding: 12, borderRadius: 'var(--radius)',
            background: 'rgba(245,158,11,0.1)', border: '1px solid var(--warning)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600 }}>
              <AlertTriangle size={15} style={{ color: 'var(--warning)' }} />
              <span>
                {unregisteredEmps.length}{' '}
                {unregisteredEmps.length === 1 ? t('early.unregWarnOne') : t('early.unregWarnMany')}
              </span>
            </div>
            <p style={{ margin: '6px 0 8px', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('early.unregWarnDesc')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {unregisteredEmps.map(e => (
                <span key={`${e.badge}_${e.facility}`} className="badge badge-warning" style={{ fontSize: 11 }}>
                  Badge {e.badge} · {e.facility || '—'}
                </span>
              ))}
            </div>
          </div>
        )}

        {employees.map((emp) => {
          const alerts = emp.days.filter(d => d.kind !== 'ok').length;
          const edited = emp.days.filter(d => isEdited(emp, d)).length;
          // Dias con la salida antes de la entrada: la tarjeta se marca en rojo para encontrarlos
          // sin ir renglon por renglon en una semana de veinte empleados.
          const badSpans = emp.days.filter(d => hasBadSpan(emp, d)).length;
          // Days that were supposed to be off and still have punches.
          const absDays = emp.days.filter(d => d.absence || d.holiday).length;
          // Whether one of the two bulk shortcuts is already the state of every day of this
          // employee — that button is then shown as the active one.
          const editableDays = emp.days.filter(isEditable);
          const allAtPunch = editableDays.length > 0
            && editableDays.every(d => times[dayKey(emp, d)] === d.punch
              && (outTimes[dayKey(emp, d)] || '') === (d.punchOut || '')
              && !(hasLunchBox(d) && lunchTouched.has(dayKey(emp, d))));
          const allAtShift = !!emp.shiftStart && editableDays.length > 0
            && editableDays.every(d => times[dayKey(emp, d)] === emp.shiftStart);
          const ek = empKey(emp);
          const unregistered = isUnregistered(emp);
          const justRegistered = registered[ek] || null;
          const regForm = regForms[ek];
          const regOpen = !!regForm;
          return (
            <div key={emp.badge + '_' + emp.facility}
              className={`empcard${badSpans > 0 ? ' is-bad' : edited > 0 ? ' is-edited' : ''}`}
              style={isIncluded(emp) ? undefined : { opacity: 0.45 }}>
              <div className="empcard-head">
                {/* Incluir o no a este empleado en el calculo. Marcado por defecto; desmarcarlo lo
                    deja fuera y conserva intacto lo que ya tenia calculado. */}
                <label className="row-check" title={t('early.includeTip')}
                  style={{ marginRight: 2 }}>
                  <input type="checkbox" checked={isIncluded(emp)} onChange={() => toggleEmp(emp)} />
                </label>
                <span className="badge badge-info" style={{ fontSize: 11 }}>Badge {emp.badge}</span>
                <span className="empcard-name">{emp.name || justRegistered?.name || '—'}</span>
                <span className="badge badge-info" style={{ fontSize: 10 }}>{emp.facility}</span>
                <span className="empcard-meta">
                  {t('early.shiftStart')} <strong>{emp.shiftStart || justRegistered?.shiftStart || '—'}</strong>
                </span>
                <span className="empcard-stats">
                  {badSpans > 0 && (
                    <span className="rowchip rowchip-bad" title={t('early.badSpanCardTip')}>
                      <AlertTriangle size={11} />
                      {badSpans} {badSpans === 1 ? t('early.badSpanDay') : t('early.badSpanDays')}
                    </span>
                  )}
                  {/* El badge marco pero no esta dado de alta en Empleados. Se muestra igual — el
                      calculo le paga horas de todas formas — pero marcado, porque lo que hay que
                      hacer con el es registrarlo, y sin verlo aqui nadie se entera de que existe. */}
                  {unregistered && (
                    <>
                      <span className="rowchip rowchip-warn" title={t('early.unregisteredTip')}>
                        <AlertTriangle size={11} />{t('early.unregistered')}
                      </span>
                      <button type="button" className="btn-secondary"
                        onClick={() => (regOpen ? closeReg(emp) : openReg(emp))}
                        title={t('early.registerTip')}
                        style={{ width: 'auto', padding: '3px 10px', fontSize: 11.5 }}>
                        <UserPlus size={12} style={{ marginRight: 4 }} />{t('early.register')}
                      </button>
                    </>
                  )}
                  {justRegistered && (
                    <span className="rowchip rowchip-ok" title={t('early.registeredTip')}>
                      <Check size={11} />{t('early.registered')}
                    </span>
                  )}
                  {/* Dado de baja y aun asi marcando: el calculo tampoco mira el status, asi que
                      este badge esta cobrando horas igual que cualquier otro. */}
                  {emp.inactive && (
                    <span className="rowchip rowchip-warn" title={t('early.inactiveTip')}>
                      <AlertTriangle size={11} />{t('early.inactive')}
                    </span>
                  )}
                  {alerts > 0 && (
                    <span className="rowchip rowchip-warn">
                      {alerts} {alerts === 1 ? t('early.alertDay') : t('early.alertDays')}
                    </span>
                  )}
                  {absDays > 0 && (
                    <span className="rowchip rowchip-pto">
                      {absDays} {absDays === 1 ? t('early.absDay') : t('early.absDays')}
                    </span>
                  )}
                  {edited > 0 && (
                    <span className="rowchip rowchip-ok">
                      {edited} {edited === 1 ? t('early.editedDay') : t('early.editedDays')}
                    </span>
                  )}
                </span>
              </div>

              <div className="empcard-body">
              {/* Alta en linea. El badge y la facilidad no se editan: son los del punch, y cambiarlos
                  daria de alta a alguien que no es el que marco. El nombre va vacio porque el punch
                  guardado no lo trae. */}
              {regOpen && (
                <div style={{
                  marginBottom: 12, padding: 12, borderRadius: 'var(--radius)',
                  background: 'rgba(245,158,11,0.08)', border: '1px solid var(--warning)',
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div>
                      <label className="form-label" style={{ fontSize: 12 }}>{t('common.name')}</label>
                      <input className="form-input" value={regForm.firstName}
                        onChange={e => setRegField(emp, 'firstName', e.target.value)} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: 12 }}>{t('common.lastName')}</label>
                      <input className="form-input" value={regForm.lastName}
                        onChange={e => setRegField(emp, 'lastName', e.target.value)} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: 12, color: !regForm.type_emp ? 'var(--danger)' : undefined }}>{t('emp.typeReq')}</label>
                      <select className="form-input" value={regForm.type_emp}
                        style={{ borderColor: !regForm.type_emp ? 'var(--danger)' : undefined }}
                        onChange={e => setRegField(emp, 'type_emp', e.target.value)}>
                        <option value="">{t('common.select')}</option>
                        <option value="PERM">PERM</option>
                        <option value="PERM-HOUR">PERM-HOUR</option>
                        <option value="PERM-SALARY">PERM-SALARY</option>
                        <option value="TEMP">TEMP</option>
                        <option value="CONTR">CONTR</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 8 }}>
                    <div>
                      <label className="form-label" style={{ fontSize: 12 }}>{t('common.department')}</label>
                      <select className="form-input" value={regForm.department_emp}
                        onChange={e => setRegField(emp, 'department_emp', e.target.value)}>
                        <option value="">{t('common.select')}</option>
                        <option value="TMS MIA">TMS MIA</option>
                        <option value="TMS AUSWH">TMS AUSWH</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: 12 }}>{t('common.company')}</label>
                      <input className="form-input" value={regForm.company_emp}
                        onChange={e => setRegField(emp, 'company_emp', e.target.value)} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: 12 }}>{t('common.location')}</label>
                      <input className="form-input" value={emp.facility || ''} readOnly style={{ opacity: 0.7 }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 8 }}>
                    <div>
                      <label className="form-label" style={{ fontSize: 12 }}>{t('emp.shiftStart')}</label>
                      <input className="form-input" type="time" value={regForm.shift_start}
                        onChange={e => setRegField(emp, 'shift_start', e.target.value)} />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: 12 }}>{t('emp.shiftEnd')}</label>
                      <input className="form-input" type="time" value={regForm.shift_end}
                        onChange={e => setRegField(emp, 'shift_end', e.target.value)} />
                    </div>
                  </div>
                  {regError[ek] && (
                    <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--danger)' }}>{regError[ek]}</p>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button type="button" className="btn-secondary" onClick={() => closeReg(emp)}
                      disabled={!!regSaving[ek]}
                      style={{ width: 'auto', padding: '6px 14px', fontSize: 12 }}>
                      <X size={13} style={{ marginRight: 4 }} />{t('common.cancel')}
                    </button>
                    <button type="button" className="btn-primary" onClick={() => saveReg(emp)}
                      disabled={!!regSaving[ek]}
                      style={{ width: 'auto', padding: '6px 14px', fontSize: 12 }}>
                      <UserPlus size={13} style={{ marginRight: 4 }} />
                      {regSaving[ek] ? t('common.saving') : t('early.registerSave')}
                    </button>
                  </div>
                </div>
              )}
              <div className="rowtable">
              <div className="rowtable-head" style={GRID}>
                <span>{t('early.colDate')}</span>
                <span>{t('early.colPunch')}</span>
                <span>{t('early.colPunchOut')}</span>
                <span />
                <span>{t('early.colFlag')}</span>
                <span>{t('early.colPaid')}</span>
                <span>{t('early.colPaidOut')}</span>
                <span style={{ textAlign: 'center' }}>{t('early.colLunch')}</span>
                <span />
              </div>

              {emp.days.map((d) => {
                const k = dayKey(emp, d);
                const flag = dayFlag(d);
                const editIn = isEditedIn(emp, d);
                const editOut = isEditedOut(emp, d);
                const editLunch = isEditedLunch(emp, d);
                const edit = editIn || editOut || editLunch; // the reset button undoes any of them
                const editable = isEditable(d);
                const badSpan = hasBadSpan(emp, d);
                const badSpanMsg = badSpan
                  ? t('early.badSpanTip')
                    .replace('{in}', fmt12(times[k]))
                    .replace('{out}', fmt12(outTimes[k]))
                  : undefined;
                return (
                  <div key={d.date} style={GRID}
                    // A day with no punches is context, not a decision: it steps back visually.
                    className={`rowtable-row${badSpan ? ' is-bad' : edit ? ' is-edited' : ''}${editable ? '' : ' is-muted'}`}>
                    <span className="cell-label">{fmtDate(d.date)}</span>
                    <span className="cell-fixed" title={t('early.punchTip')}>
                      {d.punch || <span className="cell-fixed is-empty">—</span>}
                    </span>
                    <span className={`cell-fixed${d.noOut ? ' is-warn' : ''}`}
                      title={d.noOut ? t('early.noOutTip') : t('early.punchOutTip')}>
                      {d.punchOut || '—'}
                    </span>
                    <span className="cell-divider" />
                    <span className="rowchips">
                      {/* Va primero y en rojo: es lo unico del renglon que impide calcular. */}
                      {badSpan && (
                        <span className="rowchip rowchip-bad" title={badSpanMsg}>
                          <AlertTriangle size={11} />{t('early.badSpan')} · {fmt12(times[k])} → {fmt12(outTimes[k])}
                        </span>
                      )}
                      {editLunch && (
                        <span className="rowchip rowchip-ok" title={t('early.lunchForcedTip')}>
                          <Utensils size={11} />{t('early.lunchForced')}
                        </span>
                      )}
                      {d.noOut && (
                        <span className="rowchip rowchip-warn" title={t('early.noOutTip')}>
                          <LogOut size={11} />{t('early.noOut')}
                        </span>
                      )}
                      {/* AUSWH: el dia paga menos que las horas por ley (5 h) y trae la comida
                          descontada. Alla no hay puerta de comedor, asi que nadie sabe si comio —
                          se pregunta aqui en vez de adivinarlo. Desmarcar devuelve los 30 min.
                          El chip dice lo que el dia PAGA, que es el dato con el que se decide; el
                          faltante contra la jornada no le sirve a nadie. */}
                      {d.shortDay && !lunchTouched.has(dayKey(emp, d)) && (
                        <span className="rowchip rowchip-warn" title={t('early.shortDayTip')}>
                          <Utensils size={11} />{t('early.shortDay')} {fmtHm(d.shortMins)} — {t('early.shortDayAsk')}
                        </span>
                      )}
                      {d.absence && (
                        <span className="rowchip rowchip-pto" title={d.absence.pays
                          ? `${t('early.absPaysTip')} ${d.absence.pto} h`
                          : t('early.absTip')}>
                          <Palmtree size={11} />
                          {d.absence.type || t('early.flagAbsence')}
                          {d.absence.pays ? ` · ${d.absence.pto} h` : ''}
                        </span>
                      )}
                      {/* Festivo, en su propio chip y CON las horas que paga. Van aparte de la
                          ausencia porque los dos pueden caer el mismo dia y antes el festivo se
                          perdia detras de ella. Las horas son las de ESTE empleado (su jornada, ya
                          no 8 planas), y solo salen cuando el festivo paga: en un dia que ademas
                          tiene punches el dia vale lo trabajado y el festivo no suma nada. */}
                      {d.holiday && (
                        <span className="rowchip rowchip-pto" title={d.holidayPays
                          ? `${t('early.holPaysTip') || 'Festivo pagado:'} ${d.holidayHrs} h`
                          : (t('early.holWorkedTip') || 'Festivo trabajado: el dia paga lo trabajado.')}>
                          <Palmtree size={11} />
                          {t('early.flagHoliday')}
                          {d.holidayPays && d.holidayHrs > 0 ? ` · ${d.holidayHrs} h` : ''}
                        </span>
                      )}
                      {flag && (
                        <span className={`rowchip ${flag.cls}`}>{flag.icon}{flag.text}</span>
                      )}
                      {d.authorized && (
                        <span className="rowchip rowchip-ok">
                          <Check size={11} />{t('early.authorized')}{d.authTime ? ` → ${d.authTime}` : ''}
                        </span>
                      )}
                    </span>
                    {editable ? (
                      <input
                        type="time"
                        // step=1 → el campo trabaja en HH:MM:SS. El día se paga desde el punch
                        // exacto (07:50:43, no 07:50), así que sin los segundos el campo proponía
                        // una hora distinta de la que el cálculo iba a usar.
                        step="1"
                        className={`form-input${badSpan ? ' is-bad' : editIn ? ' is-edited' : ''}`}
                        value={times[k] || ''}
                        onChange={(e) => setTime(k, e.target.value)}
                        title={badSpanMsg || t('early.paidTip')}
                      />
                    ) : (
                      // Un festivo sin punches no es un dia "sin marcar" que alguien deba arreglar:
                      // es un dia libre que se paga. La celda dice cuanto, en vez del aviso generico.
                      <span className="cell-note"
                        title={d.holidayPays
                          ? (t('early.holPaysTip') || 'Festivo pagado:')
                          : (isPtoDay(d) ? t('early.ptoIgnoredTip') : t('early.noPunchTip'))}>
                        {d.holidayPays
                          ? `${t('early.flagHoliday')} · ${d.holidayHrs} h`
                          : (isPtoDay(d) ? t('early.ptoIgnored') : t('early.noPunch'))}
                      </span>
                    )}
                    {editable ? (
                      <input
                        type="time"
                        step="1"
                        // An empty Out on a day with no exit punch is the thing to fix, so the
                        // field says so instead of looking like any other untouched cell.
                        className={`form-input${badSpan ? ' is-bad' : editOut ? ' is-edited' : (d.noOut && !outTimes[k]) ? ' is-todo' : ''}`}
                        value={outTimes[k] || ''}
                        onChange={(e) => setOutTime(k, e.target.value)}
                        title={badSpanMsg || t('early.paidOutTip')}
                      />
                    ) : <span />}
                    {hasLunchBox(d) ? (
                      <label className="row-check" title={t('early.lunchTip')}>
                        <input
                          type="checkbox"
                          checked={lunchChecked(emp, d)}
                          onChange={(e) => setLunch(k, e.target.checked)}
                        />
                      </label>
                    ) : <span />}
                    {(editable || hasLunchBox(d)) ? (
                      <button type="button"
                        onClick={() => {
                          if (editable) {
                            setTime(k, d.punch);
                            setOutTime(k, d.punchOut || '');
                          }
                          if (hasLunchBox(d)) resetLunch(k);
                        }}
                        disabled={!edit}
                        title={t('early.resetTip')}
                        className="row-ghost-btn">
                        <RotateCcw size={14} />
                      </button>
                    ) : <span />}
                  </div>
                );
              })}
              </div>

              <div className="empcard-actions">
                <button type="button" onClick={() => setEmpAll(emp, 'punch')}
                  className={allAtPunch ? 'btn-primary' : 'btn-secondary'}
                  style={{ width: 'auto', padding: '6px 14px', fontSize: 12, flex: 1 }}>
                  {allAtPunch ? <Check size={13} style={{ marginRight: 4 }} /> : <Clock size={13} style={{ marginRight: 4 }} />}
                  {t('early.allPunch')}
                </button>
                <button type="button" onClick={() => setEmpAll(emp, 'shift')} disabled={!emp.shiftStart}
                  className={allAtShift ? 'btn-primary' : 'btn-secondary'}
                  style={{ width: 'auto', padding: '6px 14px', fontSize: 12, flex: 1 }}>
                  <Check size={13} style={{ marginRight: 4 }} />{t('early.allShift')} {emp.shiftStart ? `(${emp.shiftStart})` : ''}
                </button>
              </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="modal-footer">
        <span style={{ marginRight: 'auto', alignSelf: 'center', fontSize: 12.5, color: 'var(--text-muted)' }}>
          {editedCount > 0
            ? `${editedCount} / ${dayCount} ${t('early.editedSummary')}`
            : `${dayCount} ${t('early.defaultSummary')}`}
          {excludedCount > 0 && (
            <span style={{ marginLeft: 10, color: 'var(--warn, #f59e0b)' }}>
              · {excludedCount} {t('early.excludedSummary')}
            </span>
          )}
          {badSpanDays.length > 0 && (
            <span style={{ marginLeft: 10, color: 'var(--danger)', fontWeight: 600 }}>
              · {t('early.badSpanBlocked')}
            </span>
          )}
        </span>
        <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
          <X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}
        </button>
        {/* Sin nadie marcado no hay nada que calcular: el boton se apaga en vez de mandar un lote
            vacio, que el backend aceptaria y no cambiaria nada. Con una salida anterior a su
            entrada se apaga por lo contrario: el backend SI lo aceptaria, pagaria 0 h y dejaria el
            dia en blanco en el reporte. */}
        <button type="button" className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }}
          onClick={handleConfirm}
          disabled={loading || includedCount === 0 || badSpanDays.length > 0}
          title={includedCount === 0 ? t('early.noneSelected')
            : badSpanDays.length > 0 ? t('early.badSpanBlocked') : undefined}>
          <Check size={15} style={{ marginRight: 6 }} />{loading ? t('common.saving') : t('early.continue')}
        </button>
      </div>
    </Modal>
  );
}
